import { createAsyncThunk, createSelector, createSlice, type PayloadAction, type WritableDraft } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

import {
  executeExpense,
  extractExpenseError,
  fetchExpenses,
  rematerializeExpense,
  scheduleExpense,
  type ExpenseStatus,
  type ExpenseDetailOut,
  type ExpenseExecutePayload,
  type ExpenseOut,
  type ExpenseRematerializePayload,
  type ExpenseSchedulePayload,
} from '@erp/expenses/api/expenses.api.ts';
import type { RootState } from '@store/store';

export type ExpenseLifecycleStatus = 'registered' | 'pending' | 'scheduled' | 'executed';

type LifecycleIds = Record<ExpenseLifecycleStatus, number[]>;
type LifecycleTotals = Record<ExpenseLifecycleStatus, number>;

interface LoadExpensesArgs {
  includeInactive?: boolean;
}

interface SyncCollectionPayload {
  expenses: ExpenseOut[];
  includeInactive?: boolean;
  referenceDate?: string;
}

interface ExpensesState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  includeInactive: boolean;
  entities: Record<number, ExpenseOut>;
  ids: number[];
  idsByLifecycle: LifecycleIds;
  totalsByLifecycle: LifecycleTotals;
  executedCurrentMonthTotal: number;
  lastUpdatedAt: string | null;
}

const createLifecycleIdBuckets = (): LifecycleIds => ({
  registered: [],
  pending: [],
  scheduled: [],
  executed: [],
});

const createLifecycleTotalBuckets = (): LifecycleTotals => ({
  registered: 0,
  pending: 0,
  scheduled: 0,
  executed: 0,
});

const parseAmount = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveExpenseAmount = (expense: ExpenseOut): number => {
  const candidates: Array<string | number | null | undefined> = [
    expense.amount_confirmed,
    expense.amount_estimated,
    expense.amount,
  ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined) {
      return parseAmount(candidate);
    }
  }
  return 0;
};

const normalizeExpense = (expense: ExpenseOut | ExpenseDetailOut): ExpenseOut => {
  const { executions, ...rest } = expense as ExpenseDetailOut & ExpenseOut;
  return { ...rest };
};

const resolveLifecycleStatus = (
  expense: ExpenseOut,
  referenceDate: dayjs.Dayjs,
): ExpenseLifecycleStatus | null => {
  const normalizedStatus = expense.status.toLowerCase() as Lowercase<ExpenseStatus>;

  switch (normalizedStatus) {
    case 'executed':
      return 'executed';
    case 'registered':
      return 'registered';
    case 'scheduled': {
      if (expense.next_run_at) {
        const nextRun = dayjs(expense.next_run_at);
        if (nextRun.isValid() && nextRun.isBefore(referenceDate)) {
          return 'pending';
        }
      }
      return 'scheduled';
    }
    default:
      return null;
  }
};

const recalculateDerivedState = (
  state: WritableDraft<ExpensesState>,
  referenceDate: dayjs.Dayjs = dayjs(),
): void => {
  const ids = Object.keys(state.entities)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id))
    .sort((a, b) => a - b);

  const idsByLifecycle = createLifecycleIdBuckets();
  const totalsByLifecycle = createLifecycleTotalBuckets();

  const monthStart = referenceDate.startOf('month');
  const monthEnd = referenceDate.endOf('month');
  let executedCurrentMonthTotal = 0;

  ids.forEach((id) => {
    const expense = state.entities[id];
    if (!expense) {
      return;
    }

    const lifecycle = resolveLifecycleStatus(expense, referenceDate);
    if (!lifecycle) {
      return;
    }

    idsByLifecycle[lifecycle].push(id);

    const amount = resolveExpenseAmount(expense);
    totalsByLifecycle[lifecycle] += amount;

    if (lifecycle === 'executed' && expense.last_run_at) {
      const lastRun = dayjs(expense.last_run_at);
      if (lastRun.isValid() && lastRun.isBetween(monthStart, monthEnd, null, '[]')) {
        executedCurrentMonthTotal += amount;
      }
    }
  });

  state.ids = ids;
  state.idsByLifecycle = idsByLifecycle;
  state.totalsByLifecycle = totalsByLifecycle;
  state.executedCurrentMonthTotal = executedCurrentMonthTotal;
  state.lastUpdatedAt = referenceDate.toISOString();
};

const resolveDueDateOrder = (expense: ExpenseOut): number => {
  if (expense.due_date) {
    const dueDate = dayjs(expense.due_date);
    if (dueDate.isValid()) {
      return dueDate.valueOf();
    }
  }
  return Number.POSITIVE_INFINITY;
};

const initialState: ExpensesState = {
  status: 'idle',
  error: null,
  includeInactive: false,
  entities: {},
  ids: [],
  idsByLifecycle: createLifecycleIdBuckets(),
  totalsByLifecycle: createLifecycleTotalBuckets(),
  executedCurrentMonthTotal: 0,
  lastUpdatedAt: null,
};

export const loadExpenses = createAsyncThunk<
  { expenses: ExpenseOut[]; includeInactive: boolean },
  LoadExpensesArgs | undefined,
  { rejectValue: string }
>('expenses/loadAll', async (args, { rejectWithValue }) => {
  const includeInactive = args?.includeInactive ?? false;
  try {
    const expenses = await fetchExpenses({ includeInactive });
    return { expenses, includeInactive };
  } catch (error) {
    return rejectWithValue(extractExpenseError(error));
  }
});

export const executeExpenseThunk = createAsyncThunk<
  ExpenseOut,
  { expenseId: number; payload: ExpenseExecutePayload },
  { rejectValue: string }
>('expenses/execute', async ({ expenseId, payload }, { rejectWithValue }) => {
  try {
    const detail = await executeExpense(expenseId, payload);
    return normalizeExpense(detail);
  } catch (error) {
    return rejectWithValue(extractExpenseError(error));
  }
});

export const scheduleExpenseThunk = createAsyncThunk<
  ExpenseOut,
  { expenseId: number; payload: ExpenseSchedulePayload },
  { rejectValue: string }
>('expenses/schedule', async ({ expenseId, payload }, { rejectWithValue }) => {
  try {
    const detail = await scheduleExpense(expenseId, payload);
    return normalizeExpense(detail);
  } catch (error) {
    return rejectWithValue(extractExpenseError(error));
  }
});

export const rematerializeExpenseThunk = createAsyncThunk<
  ExpenseOut,
  { expenseId: number; payload?: ExpenseRematerializePayload },
  { rejectValue: string }
>('expenses/rematerialize', async ({ expenseId, payload }, { rejectWithValue }) => {
  try {
    const detail = await rematerializeExpense(expenseId, payload);
    return normalizeExpense(detail);
  } catch (error) {
    return rejectWithValue(extractExpenseError(error));
  }
});

const expensesSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    syncFromCollection: (state, action: PayloadAction<SyncCollectionPayload>) => {
      const { expenses, includeInactive, referenceDate } = action.payload;
      state.entities = {};
      expenses.forEach((expense) => {
        const normalized = normalizeExpense(expense);
        state.entities[normalized.id] = normalized;
      });
      if (typeof includeInactive === 'boolean') {
        state.includeInactive = includeInactive;
      }
      const reference = referenceDate ? dayjs(referenceDate) : dayjs();
      recalculateDerivedState(state, reference);
      state.status = 'succeeded';
      state.error = null;
    },
    upsertExpense: (state, action: PayloadAction<ExpenseOut | ExpenseDetailOut>) => {
      const normalized = normalizeExpense(action.payload);
      state.entities[normalized.id] = normalized;
      recalculateDerivedState(state);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadExpenses.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadExpenses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.includeInactive = action.payload.includeInactive;
        state.entities = {};
        action.payload.expenses.forEach((expense) => {
          const normalized = normalizeExpense(expense);
          state.entities[normalized.id] = normalized;
        });
        recalculateDerivedState(state);
      })
      .addCase(loadExpenses.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Ocurrió un error al cargar los gastos.';
      })
      .addCase(executeExpenseThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(executeExpenseThunk.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
        recalculateDerivedState(state);
      })
      .addCase(executeExpenseThunk.rejected, (state, action) => {
        state.error = action.payload ?? 'No fue posible ejecutar el gasto.';
      })
      .addCase(scheduleExpenseThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(scheduleExpenseThunk.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
        recalculateDerivedState(state);
      })
      .addCase(scheduleExpenseThunk.rejected, (state, action) => {
        state.error = action.payload ?? 'No fue posible programar el gasto.';
      })
      .addCase(rematerializeExpenseThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(rematerializeExpenseThunk.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
        recalculateDerivedState(state);
      })
      .addCase(rematerializeExpenseThunk.rejected, (state, action) => {
        state.error = action.payload ?? 'No fue posible regenerar las ejecuciones del gasto.';
      });
  },
});

export const { clearError, syncFromCollection, upsertExpense } = expensesSlice.actions;

export const selectExpensesState = (state: RootState): ExpensesState => state?.expenses ?? initialState;

export const selectExpenseEntities = createSelector(
  selectExpensesState,
  (slice) => slice.entities,
);

export const selectExpenseIds = createSelector(selectExpensesState, (slice) => slice.ids);

export const selectExpensesStatus = createSelector(selectExpensesState, (slice) => slice.status);

export const selectExpensesError = createSelector(selectExpensesState, (slice) => slice.error);

export const selectTotalsByLifecycle = createSelector(
  selectExpensesState,
  (slice) => slice.totalsByLifecycle,
);

export const selectExecutedCurrentMonthTotal = createSelector(
  selectExpensesState,
  (slice) => slice.executedCurrentMonthTotal,
);

export const selectPendingTotal = createSelector(
  selectTotalsByLifecycle,
  (totals) => totals?.pending ?? 0,
);

export const selectPendingVariableExpensesByDueDate = createSelector(
  selectExpensesState,
  (slice) =>
    slice.idsByLifecycle.pending
      .map((id) => slice.entities[id])
      .filter((expense): expense is ExpenseOut => Boolean(expense) && expense.type === 'variable')
      .slice()
      .sort((a, b) => {
        const dueA = resolveDueDateOrder(a);
        const dueB = resolveDueDateOrder(b);
        if (dueA === dueB) {
          return a.id - b.id;
        }
        return dueA - dueB;
      }),
);

export const selectScheduledTotal = createSelector(
  selectTotalsByLifecycle,
  (totals) => totals?.scheduled ?? 0,
);

export const selectRegisteredTotal = createSelector(
  selectTotalsByLifecycle,
  (totals) => totals?.registered ?? 0,
);

export const makeSelectExpensesByLifecycle = (status: ExpenseLifecycleStatus) =>
  createSelector(selectExpensesState, (slice) =>
    (slice?.idsByLifecycle?.[status] ?? []).map((id) => slice?.entities?.[id]).filter((expense): expense is ExpenseOut => Boolean(expense)),
  );

export const makeSelectExpenseById = (expenseId: number) =>
  createSelector(selectExpenseEntities, (entities) => entities[expenseId] ?? null);

export default expensesSlice.reducer;
